import './App.css';
import './styles';
import Navigationbar from './components/navbar';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import MobileBackButton from './components/MobileBackButton/MobileBackButton';
import { ParivarProvider, useParivar } from './context/ParivarContext';
import 'bootstrap/dist/css/bootstrap.min.css';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import React, { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import LoadingLogo from './components/loader/LoadingLogo';
import { initGA, trackPageView } from './utils/analytics';
import { setPreviousPath } from './utils/navigationHistory';
// Home stays EAGER (plain import) because it is the landing page: a lazy
// chunk would only be fetched AFTER main.js evaluates, adding a serialized
// round-trip that pushes LCP out by seconds on slow networks.
import Home from './pages/Home';

// Decorative/interactive extras are lazy too so the critical-path bundle stays
// small: they load right after paint without blocking the LCP text.
const ScrollProgress = React.lazy(() => import('./components/ScrollProgress/ScrollProgress'));
const CursorGlow = React.lazy(() => import('./components/CursorGlow/CursorGlow'));
const AdvertisementPopup = React.lazy(() => import('./components/Advertisement/AdvertisementPopup'));
const ParivarFirstVisitModal = React.lazy(() => import('./components/ParivarFirstVisit/ParivarFirstVisitModal'));

// Lazy load pages
const About = React.lazy(() => import('./pages/About'));
const Contact = React.lazy(() => import('./pages/Contact'));
const NewsEvents = React.lazy(() => import('./pages/NewsEvents'));
const Publication = React.lazy(() => import('./pages/Publication'));
const Live = React.lazy(() => import('./pages/Live'));
const Bhajan = React.lazy(() => import('./pages/Bhajan'));
const Gallery = React.lazy(() => import('./pages/Gallery'));
const GalleryCategory = React.lazy(() => import('./pages/GalleryCategory'));
const Videos = React.lazy(() => import('./pages/Videos'));
const VideoCategory = React.lazy(() => import('./pages/VideoCategory'));
const BhajanCategory = React.lazy(() => import('./pages/BhajanCategory'));
const PublicationCategory = React.lazy(() => import('./pages/PublicationCategory'));
const NewsDetail = React.lazy(() => import('./pages/NewsDetail'));
const EventDetail = React.lazy(() => import('./pages/EventDetail'));
const ShareKnowledge = React.lazy(() => import('./pages/ShareKnowledge'));
const ShareKnowledgeDetail = React.lazy(() => import('./pages/ShareKnowledgeDetail'));
const ShareKnowledgeSubmit = React.lazy(() => import('./pages/ShareKnowledgeSubmit'));
const Login = React.lazy(() => import('./pages/Login'));
const Signup = React.lazy(() => import('./pages/Signup'));
const ForgotPassword = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/ResetPassword'));
const AdminInviteSetup = React.lazy(() => import('./pages/AdminInviteSetup'));
const Admin = React.lazy(() => import('./admin/Admin'));
const ParivarDetails = React.lazy(() => import('./pages/ParivarDetails'));

function App() {
  useEffect(() => {
    initGA();
  }, []);

  return (
    <div className="App">
      <div className="av-grain" aria-hidden="true" />
      <Suspense fallback={null}>
        <CursorGlow />
        <Router>
          <Suspense fallback={null}>
            <ScrollProgress />
          </Suspense>
          <ParivarProvider>
            <AppContent />
          </ParivarProvider>
        </Router>
      </Suspense>
    </div>
  );
}

// Wrapper that renders a Suspense boundary around each route element.
// This means only the actively-navigating route shows a spinner, not the
// entire page — making transitions feel much snappier.
function LazyRoute({ Component }) {
  return (
    <Suspense fallback={<LoadingLogo />}>
      <Component />
    </Suspense>
  );
}

function AppContent() {
  const location = useLocation();
  const { needsOnboarding } = useParivar();
  const showNavbar = !location.pathname.startsWith('/admin');
  const [navigating, setNavigating] = useState(false);
  const prevPath = useRef(location.pathname);

  // First-visit Parivar onboarding: appears ONLY after the advertisement
  // popup has finished (shown and closed, or skipped) so the two never stack.
  // The ad popup reports settlement via its optional `onSettled(shown)`
  // observer; a short beat gives the page time to rest before the prompt.
  const [adSettled, setAdSettled] = useState(false);
  const [pvDismissed, setPvDismissed] = useState(false);
  const [showPv, setShowPv] = useState(false);
  const handleAdSettled = useCallback(() => setAdSettled(true), []);

  useEffect(() => {
    if (!adSettled || pvDismissed || !needsOnboarding || !showNavbar) return undefined;
    const t = setTimeout(() => setShowPv(true), 320);
    return () => clearTimeout(t);
  }, [adSettled, pvDismissed, needsOnboarding, showNavbar]);

  // Flip the loader state synchronously during render (before commit) so the
  // dark loading screen and the new route are painted together — the browser
  // never draws a bare white frame between pages. Also records the path we are
  // leaving so the premium back button knows where to return.
  if (location.pathname !== prevPath.current) {
    setPreviousPath(prevPath.current);
    prevPath.current = location.pathname;
    setNavigating(true);
  }

  useEffect(() => {
    if (!navigating) return;
    const t = setTimeout(() => setNavigating(false), 400);
    return () => clearTimeout(t);
  }, [navigating]);

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  const isHome = location.pathname === '/';
  const showBack = showNavbar && !isHome;

  return (
    <>
      <ScrollToTop />
      {showNavbar && <Navigationbar />}
      {navigating && <LoadingLogo />}
      {showBack && <MobileBackButton />}
      <div className="page-enter" key={location.pathname}>
        <Routes location={location}>
          <Route path="/" element={<LazyRoute Component={Home} />} />
          <Route path="/about" element={<LazyRoute Component={About} />} />
          <Route path="/contact" element={<LazyRoute Component={Contact} />} />
          <Route path="/news-events" element={<LazyRoute Component={NewsEvents} />} />
          <Route path="/news/:slug" element={<LazyRoute Component={NewsDetail} />} />
          <Route path="/events/:slug" element={<LazyRoute Component={EventDetail} />} />
          <Route path="/publication" element={<LazyRoute Component={Publication} />} />
          <Route path="/publication-category" element={<LazyRoute Component={PublicationCategory} />} />
          <Route path="/live" element={<LazyRoute Component={Live} />} />
          <Route path="/bhajan" element={<LazyRoute Component={Bhajan} />} />
          <Route path="/bhajan-category" element={<LazyRoute Component={BhajanCategory} />} />
          <Route path="/gallery" element={<LazyRoute Component={Gallery} />} />
          <Route path="/gallery-category" element={<LazyRoute Component={GalleryCategory} />} />
          <Route path="/videos" element={<LazyRoute Component={Videos} />} />
          <Route path="/video-category" element={<LazyRoute Component={VideoCategory} />} />
          <Route path="/share-knowledge" element={<LazyRoute Component={ShareKnowledge} />} />
          <Route path="/share-knowledge/submit" element={<LazyRoute Component={ShareKnowledgeSubmit} />} />
          <Route path="/share-knowledge/:id" element={<LazyRoute Component={ShareKnowledgeDetail} />} />
          <Route path="/login" element={<LazyRoute Component={Login} />} />
          <Route path="/signup" element={<LazyRoute Component={Signup} />} />
          <Route path="/forgot-password" element={<LazyRoute Component={ForgotPassword} />} />
          <Route path="/reset-password" element={<LazyRoute Component={ResetPassword} />} />
          {/* Outside the /admin/* guard on purpose: invite links are opened
              by unauthenticated Parivar Admins before they have a password. */}
          <Route path="/admin/invite/setup" element={<LazyRoute Component={AdminInviteSetup} />} />
          <Route path="/admin/*" element={<LazyRoute Component={Admin} />} />
<Route path="/parivar/:slug" element={<LazyRoute Component={ParivarDetails} />} />
        </Routes>
      </div>
      {!location.pathname.startsWith('/admin') && (
        <Suspense fallback={null}>
          <AdvertisementPopup onSettled={handleAdSettled} />
        </Suspense>
      )}
      {!location.pathname.startsWith('/admin') && (
        <Suspense fallback={null}>
          <ParivarFirstVisitModal
            open={showPv}
            onClose={() => {
              setPvDismissed(true);
              setShowPv(false);
            }}
          />
        </Suspense>
      )}
    </>
  );
}

export default App;
